import type { FC } from "react";

import { CopyButton } from "../../common/CopyButton";

const replayDetailValueClassName =
  "mt-2 break-all text-sm font-medium text-slate-900 dark:text-slate-100";

const CompileRecordingReplayDetail: FC<{
  label: string;
  value: string | null;
}> = ({ label, value }) => (
  <div className="rounded-none border border-slate-200/80 bg-white/80 p-3 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur dark:border-white/10 dark:bg-slate-950/50 dark:shadow-[0_10px_30px_rgba(2,6,23,0.32)]">
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      {label}
    </div>
    <div className={replayDetailValueClassName}>{value ?? "Unavailable"}</div>
  </div>
);

const CompileRecordingReplayChip: FC<{
  tone: "info" | "success";
  children: string;
}> = ({ tone, children }) => {
  const toneClassName =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50/90 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
      : "border-blue-200 bg-blue-50/90 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300";

  return (
    <span
      className={`inline-flex items-center rounded-none border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${toneClassName}`}
    >
      {children}
    </span>
  );
};

const CompileRecordingReplayViewportHeader: FC = () => (
  <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.04] px-4 py-3">
    <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
    <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
    <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
      Compile Recording Replay
    </span>
  </div>
);

const CompileRecordingReplayPlaceholder: FC = () => (
  <div className="flex h-full items-center justify-center p-6">
    <div className="max-w-sm space-y-3 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-none border border-white/10 bg-white/[0.06] text-slate-200">
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14m-9 4h8a2 2 0 002-2V8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
          />
        </svg>
      </div>
      <div className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-blue-200">
          Waiting For Artifact
        </p>
        <p className="text-sm text-slate-300">
          The replay stage will appear here after the recording session uploads
          its MP4 output.
        </p>
      </div>
    </div>
  </div>
);

const CompileRecordingReplayViewport: FC<{ artifactUrl: string | null }> = ({
  artifactUrl,
}) => (
  <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-black/75 shadow-[0_30px_90px_rgba(15,23,42,0.55)]">
    <CompileRecordingReplayViewportHeader />
    <div className="relative aspect-video bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.12),transparent_32%),linear-gradient(180deg,rgba(15,23,42,0.88),rgba(2,6,23,0.96))]">
      {artifactUrl ? (
        <video
          className="h-full w-full bg-black object-contain"
          controls
          playsInline
          preload="metadata"
          src={artifactUrl}
        />
      ) : (
        <CompileRecordingReplayPlaceholder />
      )}
    </div>
  </div>
);

export const CompileRecordingReplayPanel: FC<{
  artifactUrl: string | null;
  artifactPath: string | null;
  branchName: string | null;
  recordingSessionId: string | null;
  targetBaseUrl: string | null;
}> = ({
  artifactUrl,
  artifactPath,
  branchName,
  recordingSessionId,
  targetBaseUrl,
}) => (
  <div className="relative overflow-hidden border-y border-blue-200/70 px-4 py-5 dark:border-white/10 sm:px-6">
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.16),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.14),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0.96),rgba(2,6,23,0.94))]" />
    <div className="pointer-events-none absolute inset-x-10 top-4 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
    <div className="relative grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
      <div className="rounded-none border border-white/10 bg-slate-950/65 p-3 shadow-[0_28px_80px_rgba(2,6,23,0.5)] backdrop-blur sm:p-4">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-blue-200/80">
              Replay Stage
            </div>
            <p className="max-w-2xl text-sm text-slate-300">
              Review the recorded QA pass in a contained viewport with native
              controls and preserved artifact fidelity.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <CompileRecordingReplayChip tone={artifactUrl ? "success" : "info"}>
              {artifactUrl ? "Stored MP4" : "Artifact Pending"}
            </CompileRecordingReplayChip>
            {branchName ? (
              <CompileRecordingReplayChip tone="info">
                {branchName}
              </CompileRecordingReplayChip>
            ) : null}
          </div>
        </div>

        <div className="mx-auto w-full max-w-3xl">
          <CompileRecordingReplayViewport artifactUrl={artifactUrl} />
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-none border border-white/10 bg-white/80 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.12)] backdrop-blur dark:bg-slate-950/45 dark:shadow-[0_18px_50px_rgba(2,6,23,0.3)]">
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Replay Context
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Key runtime details stay adjacent to the player so the replay is
            scannable without stretching the media edge to edge.
          </p>
        </div>

        <div className="grid gap-3">
          <CompileRecordingReplayDetail
            label="Target Runtime"
            value={targetBaseUrl}
          />
          <CompileRecordingReplayDetail
            label="Recording Session"
            value={recordingSessionId}
          />
          <CompileRecordingReplayDetail
            label="Artifact Location"
            value={artifactPath}
          />
        </div>

        {artifactUrl ? (
          <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
            <a
              className="inline-flex items-center justify-center rounded-none border border-blue-200 bg-blue-50 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-blue-700 transition-colors duration-200 hover:border-blue-300 hover:bg-blue-100 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:border-blue-400/30 dark:hover:bg-blue-500/15"
              href={artifactUrl}
              rel="noreferrer"
              target="_blank"
            >
              Open Artifact
            </a>
            <CopyButton text={artifactUrl} />
          </div>
        ) : (
          <p className="mt-auto rounded-none border border-dashed border-white/10 bg-slate-950/20 px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            Once the upload completes, direct artifact actions will appear here.
          </p>
        )}
      </div>
    </div>
  </div>
);
